import { CategoryBadge } from '@components/CategoryBadge';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import LazyVideo from '@components/LazyVideo';
import NapsterSpinner from '@components/NapsterSpinner';
import { useLowDataMode } from '@hooks/station';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { placeHolderData } from '../../config';

interface Contributor {
  id: string;
  givenName: string;
  thumbnailUrl?: string;
  videoMobileUrl?: string;
}

interface MusicCardProps {
  album: any;
  contributor?: Contributor;
  videoUrl?: string;
  posterUrl?: string;
  albumArtUrl?: string;
  albumName?: string;
  likes?: number;
  isLiked?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  onFavoriteClick?: (e: React.MouseEvent) => void;
  showFavoriteButton?: boolean;
  isUnfavoriting?: boolean;
  showPlayButton?: boolean;
  onPlayClick?: (e: React.MouseEvent) => void;
  isLoading?: boolean;
  showLikesCount?: boolean;
  compactSize?: boolean;
}

export const MusicCard = memo(
  ({
    album,
    contributor,
    videoUrl,
    posterUrl,
    albumArtUrl,
    albumName,
    likes,
    isLiked,
    onClick,
    onFavoriteClick,
    showFavoriteButton = false,
    isUnfavoriting = false,
    showPlayButton = false,
    onPlayClick,
    showLikesCount = true,
    compactSize = false,
  }: MusicCardProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLowDataMode] = useLowDataMode();
    const displayPosterUrl = posterUrl || contributor?.thumbnailUrl || '';
    const displayVideoUrl =
      videoUrl ||
      contributor?.videoMobileUrl ||
      (displayPosterUrl ? '' : placeHolderData.contributorMobileVideo);
    const displayAlbumArtUrl = albumArtUrl || placeHolderData.albumCover;
    const displayAlbumName = albumName || album?.name || t('common.unknownAlbum');
    const displayContributorName = contributor?.givenName || t('common.unknownArtist');
    const backgroundImageUrl =
      displayPosterUrl || displayAlbumArtUrl || placeHolderData.albumCover;

    const handleContributorClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (contributor?.id) {
        navigate(`/contributor/${contributor.id}`);
      }
    };

    return (
      <div
        className={`w-full snap-start relative rounded-[16px] shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] cursor-pointer hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 opacity-100 group ${
          compactSize ? 'h-[395px]' : 'h-[520px]'
        }`}
        onClick={() => onClick?.()}>
        {/* Background Video */}
        <div className="flex absolute top-0 left-0 w-full h-full rounded-[16px] overflow-hidden">
          {isLowDataMode ? (
            <img
              className="w-full h-full object-cover"
              src={backgroundImageUrl}
              alt=""
            />
          ) : (
            <LazyVideo
              className="w-full h-full object-cover"
              src={displayVideoUrl}
              poster={displayPosterUrl || undefined}
              autoPlay
              loop
              muted
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              enabled
              rootMargin="400px"
              threshold={0.1}
            />
          )}
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,1)_100%)] z-[99] rounded-[16px]" />

        {/* Music Badge */}
        <CategoryBadge category="music" />

        {/* Hover overlay with play and favorite buttons */}
        {showPlayButton && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-[100] rounded-[16px]">
            <button
              onClick={onPlayClick}
              className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full mr-3 hover:bg-white/30 transition-colors duration-200">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
                <path d="M4 2v16l12-8z" />
              </svg>
            </button>

            {showFavoriteButton && (
              <button
                onClick={onFavoriteClick}
                disabled={isUnfavoriting}
                className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200 disabled:opacity-50">
                {isUnfavoriting ? (
                  <NapsterSpinner size="small" />
                ) : isLiked ? (
                  <Likedicon width={20} height={20} />
                ) : (
                  <FavoriteIcon width={20} height={20} />
                )}
              </button>
            )}
          </div>
        )}

        {/* Bottom-Left Section: Album Cover with Vinyl Record */}
        <div className="absolute bottom-[68px] md:bottom-[128px] left-[20px] z-[101] overflow-hidden w-[172px] h-[132px]">
          {/* Vinyl Record - behind */}
          <div className="absolute top-0 left-[35px] w-[73px] h-[73px] md:w-[132px] md:h-[132px] rounded-full overflow-hidden shadow-lg z-[5]">
            <img
              src="/assets/images/album.png"
              alt={t('aria.vinylRecord')}
              className="w-full h-full object-cover"
            />
          </div>
          {/* Album Cover - on top */}
          <div className="absolute left-0 top-0 w-[73px] h-[73px] md:w-[132px] md:h-[132px] overflow-hidden bg-gray-800 shadow-lg rounded-[8px] z-[10]">
            <img
              src={displayAlbumArtUrl}
              alt={displayAlbumName}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-[9px] w-full text-left">
          <div className="text-white white-space-nowrap truncate font-[DM_Serif_Display] font-normal text-[20px] md:text-[24px] leading-[28px]">
            {displayAlbumName}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div
              className="flex items-center gap-[16px] flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleContributorClick}>
              <img
                src={
                  contributor?.thumbnailUrl ||
                  placeHolderData.contributorThumbnail
                }
                alt={displayContributorName}
                className="w-[56px] h-[56px] rounded-full flex-shrink-0 object-cover"
              />
              <span className="text-white font-[DM_Sans] font-medium text-[14px] md:text-[16px] leading-[16px]">
                {displayContributorName}
              </span>
            </div>
            {showLikesCount && likes !== undefined && onFavoriteClick && (
              <div
                className="flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onFavoriteClick}>
                {/* <span className="text-white/80 text-[12px] font-semibold leading-[normal]">
                  {likes}
                </span> */}
                {isLiked ? (
                  <Likedicon width={20} height={20} />
                ) : (
                  <FavoriteIcon width={20} height={20} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

MusicCard.displayName = 'MusicCard';
